import React, { useEffect, useRef, useState } from 'react';
import usePurchasedSubscriptions from 'src/hooks/usePurchasedSubscriptions';
import { useTranslation } from 'react-i18next';
import './SubscriptionsList.css';
import { PurchasedSubscription } from 'pages/Personal/Personal.types';
import { BookedLesson } from 'pages/Personal/components/SubscriptionsList/SubscriptionsList.types';
import { useLoader } from 'src/hooks/useLoader/LoaderContext';
import { Bundle } from 'pages/Education/Education.types';
import { format } from 'date-fns';
import useChildren from 'src/hooks/useChildren';

const SubscriptionsList: React.FC = (): React.ReactElement => {
	const {
		purchasedSubscriptions,
		getBundle,
		getSubscriptionDetails
	}                                       = usePurchasedSubscriptions(undefined, true, true);
	const {setIsLoading}                    = useLoader();
	const {t}                               = useTranslation();
	const [openIndex, setOpenIndex]         = useState<number | null>(null);
	const contentRefs                       = useRef<(HTMLDivElement | null)[]>([]);
	const [bookedLessons, setBookedLessons] = useState<BookedLesson[]>([]);
	const [bookedBundles, setBookedBundles] = useState<Bundle[]>([]);
	const [showExpired, setShowExpired]     = useState<boolean>(false);
	const { children }                      = useChildren();

	useEffect(() => {
		contentRefs.current.forEach((ref, i) => {
			if (!ref) return;

			if (openIndex === i) {
				ref.style.height = 'auto';
				ref.style.opacity = '1';

				requestAnimationFrame(() => {
					const fullHeight = ref.scrollHeight;
					ref.style.height = '0px';

					requestAnimationFrame(() => {
						ref.style.transition = 'height 1.3s ease, opacity 1.3s ease';
						ref.style.height = fullHeight + 'px';
					});
				});
			} else {
				ref.style.transition = 'height 1.3s ease, opacity 1.3s ease';
				ref.style.height = '0px';
				ref.style.opacity = '0';
			}
		});
	}, [openIndex]);

	const toggleAccordion = async (index: number, subscription: PurchasedSubscription) => {
		if (index === openIndex) {
			setOpenIndex(null);
			return;
		}

		setIsLoading(true);
		if (subscription.type !== 'SINGLE') {
			let bundleIds: number[] = [];
			if (subscription.bundleId) {
				bundleIds.push(subscription.bundleId);
			}
			if (subscription?.bundleIds && subscription?.bundleIds?.length > 0) {
				bundleIds = [...bundleIds, ...subscription.bundleIds];
			}

			const bundles: Bundle[] = [];

			if (bundleIds.length > 0) {
				await Promise.all(
					bundleIds.map(async (bundleId) => {
						const bundle = await getBundle(bundleId);
						bundles.push({
							...bundle,
							startDate: format(new Date(bundle.startDate), 'yyyy-MM-dd'),
							endDate: format(new Date(bundle.endDate), 'yyyy-MM-dd'),
							start: new Date(bundle.startDate),
							end: new Date(bundle.endDate),
						});
					})
				);
			}
			bundles.sort((a, b) => (a.start as Date).getTime() - (b.start as Date).getTime());
			setBookedBundles(bundles);
			setIsLoading(false);
			setOpenIndex(prev => (prev === index ? null : index));
		} else {
			const pageSize = subscription.lessonsCountInitial + 1 - subscription.lessonsCountLeft;
			getSubscriptionDetails(subscription.id, pageSize).then(
				(response: BookedLesson[] | any) => {
					if (response.success !== false) {
						setBookedLessons(response);
					} else {
						setBookedLessons([]);
					}
					setOpenIndex(prev => (prev === index ? null : index));
					setIsLoading(false);
				}
			).catch((_error) => {
				console.log(_error);
				setIsLoading(false);
			});
		}

	};

	const handleShowExpired = () => {
		setShowExpired(!showExpired);
	};

	return purchasedSubscriptions && purchasedSubscriptions?.length >= 0 ? (
		<>
			<div className="subscriptions-info" style={{
				position: 'relative',
			}}>
				{purchasedSubscriptions?.filter((s: any) =>
					new Date(s.endDate) >= new Date()
				).map((subscription, index) => (
					<React.Fragment key={subscription.id}>

						<div key={subscription.id} className={'subscriptions-container'}>
							<div className="info">
								{subscription.subscriptionName}
								{subscription.type === 'SINGLE' && <button className={'pink'}>
									{subscription.lessonsCountInitial} {t('pages.personal.lessons')}
								</button>}
							</div>
							<div className="info">
								{
									subscription.type === 'SINGLE' ? (
											<>
												{t('pages.personal.remainingLessons')}
												<button className="yellow">
													{subscription.lessonsCountLeft} {t('pages.personal.lessons')}
												</button>
											</>)
										: (<>
											{t('pages.personal.startDate')}
											<span className="end-date">{subscription.startDate}</span>
										</>)
								}
							</div>
							<div className="info">
								{t('pages.personal.toBeUsed')}
								<span className="end-date">{subscription.endDate}</span>
							</div>
							<div className="info justify-self-end">
								<div className="details-toggle" onClick={() => toggleAccordion(index, subscription)}>
									{t('pages.personal.view-details')}
									<div className="chevron">
										<svg xmlns="http://www.w3.org/2000/svg"
												 width="512"
												 height="298"
												 shapeRendering="geometricPrecision"
												 textRendering="geometricPrecision"
												 imageRendering="optimizeQuality"
												 fill="#1816DF"
												 style={{
													 transition: 'transform 0.7s ease',
													 transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)'
												 }}
												 viewBox="0 0 512 298.04">
											<path fillRule="nonzero"
														d="M12.08 70.78c-16.17-16.24-16.09-42.54.15-58.7 16.25-16.17 42.54-16.09 58.71.15L256 197.76 441.06 12.23c16.17-16.24 42.46-16.32 58.71-.15 16.24 16.16 16.32 42.46.15 58.7L285.27 285.96c-16.24 16.17-42.54 16.09-58.7-.15L12.08 70.78z"/>
										</svg>
									</div>
								</div>
							</div>
						</div>
						<div ref={(el) => (contentRefs.current[index] = el)}
								 className="accordion-content"
								 style={{
									 height: 0,
									 overflow: 'hidden',
									 transition: 'height 1.3s ease, opacity 1.3s ease',
									 opacity: 0,
									 width: '100%',
								 }}
						>
							<table className={'details-table'}>
								<thead>
								<tr>
									<th>{t('details-table.header.lesson')}</th>
									{subscription.type === 'SINGLE' && <th>{t('details-table.header.date')}</th>}
									{subscription.type !== 'SINGLE' && <th style={{borderInline: 'none'}} />}
									<th>{t('details-table.header.time')}</th>
									{subscription.type === 'SINGLE' && <th>{t('details-table.header.childName')}</th>}
								</tr>
								</thead>
								<tbody>
								{subscription.type === 'SINGLE' && bookedLessons?.length > 0 && bookedLessons?.map((bookedLesson: BookedLesson, index: number) => {
									return <tr key={index}>
										<td>{bookedLesson.name}</td>
										<td>{bookedLesson.bookingDate}</td>
										<td>{bookedLesson.startTime.slice(0, -3)} - {bookedLesson.endTime.slice(0, -3)}</td>
										<td>{bookedLesson.childFirstName} {bookedLesson.childLastName}</td>
									</tr>;
								})}
								{subscription.type !== 'SINGLE' && bookedBundles?.length > 0 && bookedBundles?.map((bundle: Bundle, index: number) => {
									return <React.Fragment key={`bundle-${index}`}>
										{subscription.type !== 'SINGLE' && <tr>
											<td colSpan={3}>
												<div style={{textAlign: 'center'}}>
													<strong>
														{
															children?.find((child: any) => child.id === subscription.childId).firstname
														}&nbsp;{
															children?.find((child: any) => child.id === subscription.childId).lastname
														}{
															subscription.type === 'CAMP' && (<>, {format(new Date(bundle.startDate), 'dd.MM.yyyy')} - {format(new Date(bundle.endDate), 'dd.MM.yyyy')}</>)
														}
													</strong>
												</div>
											</td>
										</tr>}
										{
											bundle.lessonSchedules
												.sort((a, b) => (new Date(a.startDate)).getTime() - (new Date(b.startDate)).getTime())
												.map((lesson, idx) => {
												return <tr key={`lesson-${index}-${idx}`}>
													<td style={{width: '50%'}}>
														{lesson.name}
													</td>
													<td style={{width: '30%'}}>
														{lesson.dayOfWeek}
													</td>
													<td style={{width: '20%'}}>
														{lesson.startTime.slice(0, -3)} - {lesson.endTime.slice(0, -3)}
													</td>
												</tr>;
											})
										}
									</React.Fragment>;
								})}


								{(
									(subscription.type === 'SINGLE' && bookedLessons?.length == 0) ||
									(subscription.type !== 'SINGLE' && bookedBundles?.length == 0)
								) && <tr>
									<td colSpan={5} style={{ textAlign: 'center' }}>
										{t('details-table.no-booked-lessons')}
									</td>
								</tr>}
								</tbody>
							</table>
						</div>
					</React.Fragment>
				))}
			</div>
			<div className={'subscriptions-info flex flex-end'}>
				<button className={'active'} onClick={handleShowExpired}>
					{showExpired ? t('pages.personal.hidePastSubscriptions') : t('pages.personal.showPastSubscriptions')}
				</button>
			</div>
			{showExpired && <div className="subscriptions-info" style={{
				position: 'relative',
			}}>
				{purchasedSubscriptions?.filter((s: any) =>
					new Date(s.endDate) < new Date()
				).map((subscription, index) => (
					<React.Fragment key={subscription.id}>

						<div key={subscription.id} className={'subscriptions-container'}>
							<div className="info">
								{subscription.subscriptionName}
								<button className={'pink'}>
									{subscription.lessonsCountInitial} {t('pages.personal.lessons')}
								</button>
							</div>
							<div className="info">
								{t('pages.personal.remainingLessons')}
								<button className="yellow">
									{subscription.lessonsCountLeft} {t('pages.personal.lessons')}
								</button>
							</div>
							<div className="info">
								{t('pages.personal.toBeUsed')}
								<span className="end-date">{subscription.endDate}</span>
							</div>
							<div className="info">
								<div className="details-toggle" onClick={() => toggleAccordion(index, subscription)}>
									{t('pages.personal.view-details')}
									<div className="chevron">
										<svg xmlns="http://www.w3.org/2000/svg"
												 width="512"
												 height="298"
												 shapeRendering="geometricPrecision"
												 textRendering="geometricPrecision"
												 imageRendering="optimizeQuality"
												 fill="#1816DF"
												 style={{
													 transition: 'transform 0.7s ease',
													 transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)'
												 }}
												 viewBox="0 0 512 298.04">
											<path fillRule="nonzero"
														d="M12.08 70.78c-16.17-16.24-16.09-42.54.15-58.7 16.25-16.17 42.54-16.09 58.71.15L256 197.76 441.06 12.23c16.17-16.24 42.46-16.32 58.71-.15 16.24 16.16 16.32 42.46.15 58.7L285.27 285.96c-16.24 16.17-42.54 16.09-58.7-.15L12.08 70.78z"/>
										</svg>
									</div>
								</div>
							</div>
						</div>
						<div ref={(el) => (contentRefs.current[index] = el)}
								 className="accordion-content"
								 style={{
									 height: 0,
									 overflow: 'hidden',
									 transition: 'height 1.3s ease, opacity 1.3s ease',
									 opacity: 0,
									 width: '100%',
								 }}
						>
							<table className={'details-table'}>
								<thead>
								<tr>
									<th>{t('details-table.header.lesson')}</th>
									<th>{t('details-table.header.date')}</th>
									<th>{t('details-table.header.time')}</th>
									<th>{t('details-table.header.childName')}</th>
									<th>{t('details-table.header.teacherName')}</th>
								</tr>
								</thead>
								<tbody>
								{bookedLessons?.length > 0 ? bookedLessons?.map((bookedLesson: BookedLesson, index: number) => {
										return <tr key={index}>
											<td>{bookedLesson.name}</td>
											<td>{bookedLesson.bookingDate}</td>
											<td>{bookedLesson.startTime.slice(0, -3)} - {bookedLesson.endTime.slice(0, -3)}</td>
											<td>{bookedLesson.childFirstName} {bookedLesson.childLastName}</td>
											<td>{bookedLesson.teacherFirstName} {bookedLesson.teacherLastNameName}</td>
										</tr>;
									}) :
									<tr>
										<td colSpan={5} style={{ textAlign: 'center' }}>
											{t('details-table.no-booked-lessons')}
										</td>
									</tr>}
								</tbody>
							</table>
						</div>
					</React.Fragment>
				))}
			</div>}
		</>
	) : <></>;
};

export default SubscriptionsList;
